// Copyright 2023, Common Good Learning Tools LLC
import { mapState, mapGetters } from 'vuex'
import { unzipSync } from 'fflate'

export default {
	data() { return {
	}},
	computed: {
		...mapState([]),
		...mapGetters([]),
	},
	watch: {
	},
	methods: {
		async import_from_cc() {
			let file
			if (empty(window.import_from_cc_file)) {
				// prompt for file to import
				file = await U.prompt_promise({
					text: '<p>Use the file upload interface below to find and process the common cartridge file.</p><p>Most common cartridge files will have the extension .imscc, but .zip and .xml files may also be acceptable.</p>',
					promptType: 'file',
					acceptText: 'Process Common Cartridge File',
					cancelText: 'Cancel'
				})
				if (!file) return

				// for debugging, uncomment the following line so that we don't have to re-choose the manifest file every time
				// window.import_from_cc_file = file
			} else {
				file = window.import_from_cc_file
			}
			console.warn('got it', file)

			// get file contents
			let file_contents
			// allow for the user to upload a .zip or .imscc archive, or an extracted xml file (imsmanifest.xml)
			if (/\.(imscc|zip)$/.test(file.name) == true) {
				// console.warn('zip')
				file_contents = await this.extract_manifest(file)

			} else if (/\.(xml)$/.test(file.name) == true) {
				// console.warn('xml')
				file_contents = await this.read_file_promise(file)

			} else {
				this.$alert('You must upload an .imscc, .zip, or .xml file (.xml). The file you attempted to process doesn’t have the right extension.')
				return
			}

			if (!file_contents) {
				this.$alert('The uploaded file could not be processed.')
				return
			}
			// console.warn('file_contents', file_contents)

			// parse the XML
			let $xml = $(this.parse_xml(file_contents))
			console.log($xml)
			// window.$xml = $xml

			// try to find the root toc item in the cc $xml
			// let toc_item = $xml.find('organization[structure="rooted-hierarchy"] item[identifier=root]')
			let toc_item = $xml.find('organization[structure="rooted-hierarchy"] item')
			if (toc_item.length == 0) {
				this.$alert('The Thin Common Cartridge file could not be processed (error 483)')
				// this.$alert('The Thin Common Cartridge file could not be processed (error 483)').then(()=>this.import_from_cc())
				return
			} else {
				toc_item = toc_item.first()
			}

			// U.loading_start('Processing Common Cartridge file; this may take some time...')
			// await this.async_wait(100)

			let resource_collection_json
			let resource_ids = {}
			let resources = {}
			// extract toc tree and resources. we have to delay starting the process a bit to allow the loading indicator/message to appear
			// first we extract the tree using this recursive fn
			resource_collection_json = this.get_toc_item(resource_ids, toc_item[0], 0, '')

			// get the manifest resource
			let manifest_resource = this.get_manifest_resource($xml, resource_collection_json)
			resources[resource_collection_json.f] = manifest_resource
			// resource_ids[resource_collection_json.f] = manifest_resource

			// get the resources and go through them one by one
			let teacher_count = 0, student_count = 0, unknown_count = 0
			let $resources = $xml.find('resources')
			for (let resource_node of $resources[0].childNodes) {
				// skip non-element nodes
				if (resource_node.nodeType != resource_node.ELEMENT_NODE) {
					let tc = $.trim(resource_node.textContent)
					if (!empty(tc)) {
						console.warn(`non-empty node of type ${resource_node.nodeType}: ${tc}`)
					}
					continue
				}

				let identifier = resource_node.getAttribute('identifier')

				// check to make sure this is one of the resource_ids we need to get
				if (resource_ids[identifier]) {
					// console.log('cc_parse_resource')
					let resource = this.cc_parse_resource(resource_ids, resource_node, identifier)
					resources[identifier] = resource

					// if teacher-facing, store "i:1" ('i' meaning 'instructor') in all nodes for this resource (i.e. in the resource_collection_json)
					if (resource.teacherOnly) {
						for (let node of resource_ids[identifier]) {
							node.i = 1
						}
						++teacher_count
					} else {
						++student_count
					}

					// delete from resource_ids, so that when we finish getting all resources we can tell if we're missing anything
					delete resource_ids[identifier]

				} else {
					console.warn('found resource that wasn’t in tree: ' + identifier)
					++unknown_count
				}
			}
			
			for (let identifier in resource_ids) {
				console.warn('didn’t find resource for identifier: ' + identifier)
			}

			console.log(`found ${teacher_count} teacher-facing and ${student_count} student-facing`)
			console.log(resource_collection_json)
			console.log(resources)

			let satchel_script = this.cc_create_satchel_script_lines('', 1, resource_collection_json, resources)
			// console.log('satchel_script', satchel_script)

			// U.download_json_file(resource_collection_json, 'TCC-TREE-' + this.learning_progression.title)
			// U.download_json_file(resources, 'TCC-RESOURCES-' + this.learning_progression.title)

			U.loading_stop()

			// tell the user what happened...
			let msg = `Found ${teacher_count} teacher-facing and ${student_count} student-facing resources.`
			if (unknown_count > 0) msg += `<br><b>${unknown_count} items were not in the tree.</b>`
			msg += `<br>(See browser debugger to inspect data)`
			msg += `<br><br>Click PASTE TO IMPORT to paste the Satchel Script for these resources into the Batch Import tool.`
			let paste_confirm = await U.confirm_promise({
				title: 'Processing Successful',
				text: msg,
				acceptText: 'PASTE TO IMPORT',
			})
			if (!paste_confirm) return

			// set raw_text_for_import to the satchel_script
			this.raw_text_for_import = satchel_script
		},

		async_wait(ms) {
			return new Promise((resolve, reject) => {
				setTimeout(x=>{
					resolve()
				}, ms)
			})
		},

		read_file_promise(file) {
			return new Promise((resolve, reject) => {
				let reader = new FileReader()
				reader.onload = e => {
					resolve(e.target.result)
				}
				reader.onerror = e => {
					console.error('reader error:', e)
					reject()
				}
				reader.readAsText(file)
			})
		},

		async extract_manifest(file) {
			let buffer = await file.arrayBuffer()
			let uint8_array = new Uint8Array(buffer)
			let target_file = 'imsmanifest.xml'

			// unzipSync can take an options object to filter specific files
			let decompressed_data = unzipSync(uint8_array, {
				filter(file_info) {
					return file_info.name === target_file
				}
			})

			let manifest_uint8 = decompressed_data[target_file]

			if (!manifest_uint8) {
				console.error('File not found in zip:', target_file)
				return
			}

			// Convert Uint8Array to string
			let decoder = new TextDecoder()
			let manifest_string = decoder.decode(manifest_uint8)

			// console.log('Manifest Content:', manifest_string)
			return manifest_string
		},

		parse_xml(xml_text) {
			let parser = new DOMParser()
			let xml_doc = parser.parseFromString(xml_text, 'text/xml')
			
			let error_node = xml_doc.querySelector('parsererror')
			if (error_node) {
				console.warn('XML Parsing Error: ' + error_node.textContent)
				return false
			}

			return xml_doc
		},

		cc_get_text_from_child_tags(node, arr, obj, key) {
			let child_node = node
			// search down for the tag_names given in arr
			for (let tag_name of arr) {
				child_node = child_node.getElementsByTagName(tag_name)
				if (child_node.length > 0) {
					child_node = child_node[0]
				} else {
					break
				}
			}
			// if we don't find the child_node, do nothing
			if (empty(child_node) || empty(child_node.textContent)) {
				return ''
			}

			// if obj is 'text', return the trimmed content
			if (obj == 'text') return $.trim(child_node.textContent)

			// if obj is 'node', return the node
			if (obj == 'node') return child_node

			// else we should have received obj/key; set key to the trimmed content
			if (!empty(obj)) {
				obj[key] = $.trim(child_node.textContent)
			}
		},

		cc_parse_resource(resource_ids, resource_node, identifier) {
			// note that we use the identifierref from the CC file as the externalIdentifier; if the tcc file is re-uploaded, old resources will be overwritten (which is what we want)
			let resource_json = {
				// externalIdentifier: identifier,	// R: resource_id
			}

			// we should always have resource_ids[identifier], because we only call cc_parse_resource if resource_ids[identifier] is nonempty
			// I can't remember why we store this as an array, but we do
			let resource_json_from_manifest = resource_ids[identifier][0]

			// if we got the title in resource_json_from_manifest, save it (the title might or might not be in the resource item too; if it is we'll overwrite with what is there)
			if (!empty(resource_json_from_manifest.t)) {
				resource_json.fullStatement = resource_json_from_manifest.t		// R: description
			}
			// ditto for teacher-facing flag
			if (resource_json_from_manifest.i == 1) {
				resource_json.teacherOnly = true		// R: teacher_facing
			}

			// look for some data in the metadata / lomr:lom / lomr:general area
			let lomr_general = this.cc_get_text_from_child_tags(resource_node, ['metadata', 'lomr:lom', 'lomr:general'], 'node')
			if (lomr_general.children?.length > 0) for (let node of lomr_general.children) {
				if (node.tagName == 'lomr:title') {
					this.cc_get_text_from_child_tags(node, ['lomr:string'], resource_json, 'fullStatement')		// R: description
				}
				if (node.tagName == 'lomr:description') {
					this.cc_get_text_from_child_tags(node, ['lomr:string'], resource_json, 'description')		// R: long_description
				}
				if (node.tagName == 'lomr:identifier') {
					if (this.cc_get_text_from_child_tags(node, ['lomr:catalog'], 'text') == 'thumbnailurl') {
						this.cc_get_text_from_child_tags(node, ['lomr:entry'], resource_json, 'thumbnail')
					}
				}
			}

			// look for some data in the metadata / lomr:lom / lomr:educational area
			let lomr_ed = this.cc_get_text_from_child_tags(resource_node, ['metadata', 'lomr:lom', 'lomr:educational'], 'node')
			if (lomr_ed.children?.length > 0) for (let node of lomr_ed.children) {
				if (node.tagName == 'lomr:typicalAgeRange') {
					this.cc_get_text_from_child_tags(node, ['lomr:string'], resource_json, 'ageRange')	// we can try to parse this into educationLevel
				}
				if (node.tagName == 'lomr:learningResourceType') {
					this.cc_get_text_from_child_tags(node, ['lomr:value'], resource_json, 'ocxType')	// we can try to parse this into educationLevel
				}
				if (node.tagName == 'lomr:intendedEndUserRole') {
					if (this.cc_get_text_from_child_tags(node, ['lomr:source'], 'text') == 'IMSGLC_CC_Rolesv1p2') {
						// if this node's lomr:value is 'Instructor', it's teacher-facing; otherwise assume student-facing
						let role = this.cc_get_text_from_child_tags(node, ['lomr:value'], 'text')
						resource_json.teacherOnly = ((''+role).toLowerCase() == 'instructor')	// teacher_facing
					}
				}
			}

			// if the resource has an lticc tag, it's an lti link
			let lti_node = resource_node.getElementsByTagName('lticc:cartridge_basiclti_link')[0]
			if (!empty(lti_node)) {
				resource_json.deliveryMechanism = 'lti'		// R: type
				resource_json.ltiParams = {}		// R: lti_params

				// look for the fullStatement and description in the lti data in case we didn't get it above
				this.cc_get_text_from_child_tags(lti_node, ['blti:title'], resource_json, 'fullStatement')		// R: description
				this.cc_get_text_from_child_tags(lti_node, ['blti:description'], resource_json, 'description')	// R: long_description

				// secure_launch_url should always be there; store in the ltiParams
				this.cc_get_text_from_child_tags(lti_node, ['blti:secure_launch_url'], resource_json.ltiParams, 'ltiLaunchURL')

				// lti custom tags: there may be multiple of these, so get the node, and if it exists and has children...
				let custom_node = this.cc_get_text_from_child_tags(lti_node, ['blti:custom'], 'node')
				if (!empty(custom_node) && custom_node.children.length > 0) {
					for (let custom_prop_node of custom_node.children) {
						// check that the child is called 'lticm:property', and extract the name attribute and text
						if (custom_prop_node.tagName == 'lticm:property') {
							let prop_name = 'custom_' + custom_prop_node.getAttribute('name')
							resource_json.ltiParams[prop_name] = $.trim(custom_prop_node.textContent)	// R: lti_params
						}
					}
				}
			}

			// else if it has a wl:webLink tag, it's a bare web link
			if (empty(resource_json.ltiParams?.ltiLaunchURL)) {
				let wl_node = resource_node.getElementsByTagName('wl:webLink')[0]
				if (!empty(wl_node)) {
					resource_json.deliveryMechanism = 'openURL'

					// look for the fullStatement and description in the lti data in case we didn't get it above
					this.cc_get_text_from_child_tags(wl_node, ['wl:title'], resource_json, 'fullStatement')		// R: description
					this.cc_get_text_from_child_tags(wl_node, ['wl:description'], resource_json, 'description')	// R: long_description

					let url_node = wl_node.getElementsByTagName('wl:url')[0]
					if (!empty(url_node)) resource_json.resourceURL = url_node.getAttribute('href')
				}
			}

			// CASE standards: there may be multiple of these, so get the node, and if it exists and has children...
			// new (2025) HMH cc's don't have 'csm:'
			resource_json.associations = []
			let standard_nodes_parent = this.cc_get_text_from_child_tags(resource_node, ['metadata', 'csm:curriculumStandardsMetadataSet', 'csm:curriculumStandardsMetadata', 'csm:setOfGUIDs'], 'node')
			if (empty(standard_nodes_parent)) standard_nodes_parent = this.cc_get_text_from_child_tags(resource_node, ['metadata', 'curriculumStandardsMetadataSet', 'curriculumStandardsMetadata', 'setOfGUIDs'], 'node')
			if (!empty(standard_nodes_parent) && standard_nodes_parent.children.length > 0) {
				// each child should have structure `<labelledGUID><label>xxx</label><GUID>yyy</GUID></labelledGUID>`
				for (let standard_node of standard_nodes_parent.children) {
					// check that the child is called 'labelledGUID', and extract the text of the GUID tag
					if (standard_node.tagName.includes('labelledGUID')) {
						// push case_identifier into array in resource_json
						let case_identifier = this.cc_get_text_from_child_tags(standard_node, ['csm:GUID'], 'text')
						if (empty(case_identifier)) case_identifier = this.cc_get_text_from_child_tags(standard_node, ['GUID'], 'text')
						
						if (!empty(case_identifier)) {
							// TODO: allow for looking this up to store a better association...
							resource_json.associations.push(case_identifier)
						}
					}
				}
			}

			return resource_json
		},

		get_toc_item(resource_ids, item_node, level, path) {
			if (empty(item_node.children)) return null

			// skip safari playlist items (and playlist children)
			// this.cc_get_text_from_child_tags(item_node, ['metadata', 'lomm:lom', 'lomm:relation', 'lomm:kind', 'lomm:source'], 'text') || 
			let source = this.cc_get_text_from_child_tags(item_node, ['metadata', 'lomm:lom', 'lomm:educational', 'lomm:learningResourceType', 'lomm:source'], 'text')
			if (source == 'SAFARI Montage') {
				console.error('skipping safari toc item: ' + item_node.getAttribute('identifier'))
				return null
			}

			// if item has a path attribute, add to path
			let item_path = item_node.getAttribute('path')
			if (!empty(item_path)) path = `${path}/${item_path}`

			let o = {}
			for (let x = 0; x < item_node.children.length; ++x) {
				let child_node = item_node.children[x]

				// extract title
				if (child_node.tagName == 'title') o.t = $.trim(child_node.textContent)

				// get child items
				if (child_node.tagName == 'item') {
					let child = this.get_toc_item(resource_ids, child_node, level + 1, path)
					if (!empty(child)) {
						if (empty(o.c)) o.c = []
						o.c.push(child)
					}
				}

				// if the item has metadata,...
				if (child_node.tagName == 'metadata') {
					// then if we have an identifierref, set up to process the metadata
					let identifierref = item_node.getAttribute('identifierref')
					if (!empty(identifierref)) {
						o.r = identifierref

						// we do this so that a) we know what resource_ids are in the tree, and b) we can transfer "teacher-facing" values to the nodes after processing the resources (??)
						if (!resource_ids[identifierref]) {
							resource_ids[identifierref] = []
						}
						resource_ids[identifierref].push(o)
					}
				}

				// note that the item will likely have either child items *or* a resource; but handle the case where we have both??
			}

			// if the item has an identifierref and we didn't set up a resource above (because we found metadata), set up a resource now
			let identifierref = item_node.getAttribute('identifierref')
			if (!empty(identifierref) && !o.r) {
				o.r = identifierref

				// if path is not empty, set it
				if (!empty(path)) o.path_start = path

				// we do this so that we know what resource_ids are in the tree
				if (!resource_ids[identifierref]) {
					resource_ids[identifierref] = []
				}
				resource_ids[identifierref].push(o)
			}

			// if we didn't get a title or children, return null (not sure if this would happen...)
			if (empty(o.t) && empty(o.c)) return null

			// if we did get children, store the identifier of the item as 'f'; we use these to mark "folders" of items to include for certain units
			if (!empty(o.c)) {
				o.f = item_node.getAttribute('identifier')
			}

			// hack for hmh-physical-science-from-safari: if `path` includes '_TE', it's teacher-facing
			// if (path.includes('_TE')) {
			// 	o.i = 1
			// }

			return o
		},

		get_manifest_resource($xml, top_node) {
			// look for metadata about the manifest, which we use for the top-level folder
			// look for a manifest identifier, to use as the externalIdentifier of the whole collection (the "collection resource" in the old Henry model); if not found use a uuid
			let $manifest_node = $xml.find('manifest')
			let resource_collection_resource_id = $manifest_node.attr('identifier')
			if (empty(resource_collection_resource_id)) resource_collection_resource_id = U.new_uuid()

			// look for a node with the following identifiers for a title and possibly description of the collection
			let manifest_title = this.cc_get_text_from_child_tags($manifest_node[0], ['metadata', 'lomm:lom', 'lomm:general', 'lomm:title', 'lomm:string'], 'text')
			if (empty(manifest_title)) manifest_title = `Collection ${resource_collection_resource_id}`
			// set f (the id) and t (the title) for the top-level folder
			top_node.f = resource_collection_resource_id
			top_node.t = manifest_title

			// other stuff that might be in the top-level manifest metadata
			let manifest_resource = {
				fullStatement: manifest_title,
			}

			this.cc_get_text_from_child_tags($manifest_node[0], ['metadata', 'lomm:lom', 'lomm:general', 'lomm:description', 'lomm:string'], manifest_resource, 'description')
			console.warn('manifest_resource', manifest_resource)
			return manifest_resource
		},

		cc_create_satchel_script_lines(satchel_script, level, node, resources) {
			// for a folder...
			if (!empty(node.f)) {
				// add satchel_script for folder itself; note that all folders might not have a resource
				satchel_script += this.cc_lines_for_item(level, node, resources[node.f])

				// then add children
				for (let child of node.c) {
					satchel_script = this.cc_create_satchel_script_lines(satchel_script, level + 1, child, resources)
				}

			// else it's a child, so add it
			} else {
				satchel_script += this.cc_lines_for_item(level, node, resources[node.r])
				// console.log(`${satchel_script.s.length} added child: ${node.t}`)
			}

			return satchel_script
		},

		cc_lines_for_item(level, node, resource) {
			let lines = ''

			// item type
			if (!empty(node.f)) lines += 'Folder '
			else lines += 'Learning_Resource '

			// level
			for (let i = 0; i < level; ++i) lines += '*'
			lines += ' '

			// fullStatement (title)
			lines += (resource?.fullStatement || node.t) + '\n'

			// add folder or resource externalIdentifier
			if (!empty(node.f)) lines += `EXT-externalIdentifier: ${node.f}\n`
			else lines += `EXT-externalIdentifier: ${node.r}\n`

			// other extensions
			if (!empty(resource)) {
				for (let key in resource) {
					if (key == 'fullStatement') continue
					if (key == 'associations') continue

					let s = (typeof(resource[key]) == 'object') ? JSON.stringify(resource[key]) : resource[key]
					lines += `EXT-${key}: ${s}\n`
				}
			}

			// TODO: associations

			return lines
		},
	}
}